import React from "react";
import { Helmet } from "react-helmet-async";
import { RouteSeo, SITE_NAME, DEFAULT_IMAGE, SITE_ORIGIN, canonicalUrl } from "./routeSeo.ts";

interface SeoProps {
    entry: RouteSeo;
}

/**
 * Sets per-route document head tags (title, description, canonical, Open Graph,
 * Twitter) at runtime. The build-time prerender script (scripts/prerender.js)
 * bakes the same values into the static HTML from the same manifest, so crawlers
 * and no-JS clients see identical metadata.
 */
export function Seo({ entry }: SeoProps) {
    const canonical = canonicalUrl(entry.slug);
    const image = SITE_ORIGIN + DEFAULT_IMAGE;

    return (
        <Helmet>
            <title>{entry.title}</title>
            <meta name="description" content={entry.description} />
            <link rel="canonical" href={canonical} />

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={entry.title} />
            <meta property="og:description" content={entry.description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={entry.title} />
            <meta name="twitter:description" content={entry.description} />
            <meta name="twitter:image" content={image} />

            {entry.faq && entry.faq.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: entry.faq.map((item) => ({
                            "@type": "Question",
                            name: item.q,
                            acceptedAnswer: { "@type": "Answer", text: item.a },
                        })),
                    })}
                </script>
            )}
        </Helmet>
    );
}

export default Seo;
